import { Fragment, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFeatureFlag, useToggleFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  useJobDigestLogs,
  useJobDigestPreview,
  useDigestDeliveryStats,
} from "@/hooks/useJobDigestLogs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { RefreshCw, Send, Power, AlertTriangle, Clock, Users, Mail, PlayCircle } from "lucide-react";

const FLAG = "job_digest_enabled";
const RESEND_MONTHLY_QUOTA = 3000;

function formatDuration(ms: number | null) {
  if (!ms && ms !== 0) return "-";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    success: { label: "Sucesso", className: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
    partial: { label: "Parcial", className: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
    error: { label: "Erro", className: "bg-destructive/15 text-destructive border-destructive/30" },
    skipped: { label: "Ignorado", className: "bg-muted text-muted-foreground border-border" },
  };
  const cfg = map[status] ?? { label: status, className: "bg-muted text-muted-foreground" };
  return <Badge variant="outline" className={cfg.className}>{cfg.label}</Badge>;
}

export function JobDigestPanel() {
  const { isEnabled, updatedAt, isLoading: flagLoading } = useFeatureFlag(FLAG);
  const toggle = useToggleFeatureFlag(FLAG);
  const { data: logs = [], isLoading, refetch, isFetching } = useJobDigestLogs(50);
  const { data: preview = [], isLoading: previewLoading } = useJobDigestPreview();
  const { data: stats, refetch: refetchStats } = useDigestDeliveryStats();
  const [sending, setSending] = useState(false);
  const [batchLimit, setBatchLimit] = useState(300);
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleToggle = async (next: boolean) => {
    try {
      await toggle.mutateAsync(next);
      toast({
        title: next ? "Envio automático ativado" : "Envio automático pausado",
        description: next
          ? "O digest voltará a ser enviado no próximo agendamento (a cada 5 dias)."
          : "As execuções agendadas serão ignoradas até você reativar.",
      });
    } catch (e: any) {
      toast({ title: "Erro ao atualizar", description: e.message, variant: "destructive" });
    }
  };

  const handleSendNow = async (isContinuation = false) => {
    const msg = isContinuation
      ? `Continuar o envio para os próximos ${batchLimit} destinatários pendentes?`
      : `Enviar o digest agora para até ${batchLimit} destinatários (quem já recebeu estas vagas é ignorado)?`;
    if (!window.confirm(msg)) return;
    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("enviar-digest-vagas", {
        body: { triggered_by: isContinuation ? "manual-continuacao" : "manual", force: true, batch_limit: batchLimit },
      });
      if (error) throw error;
      if (data?.skipped) {
        toast({
          title: "Envio ignorado",
          description:
            data.reason === "no_jobs"
              ? "Não há vagas ativas para enviar."
              : data.reason === "no_new_jobs"
              ? "Todos os destinatários já receberam estas vagas."
              : "Nenhum destinatário elegível.",
        });
      } else if (data?.cota_esgotada) {
        toast({
          title: "Cota mensal do Resend esgotada",
          description: `Enviados ${data?.enviados ?? 0} antes de parar. Restam ${data?.pendentes ?? 0} pendentes — retome no próximo ciclo ou aumente o plano.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: data?.completo ? "Digest concluído" : "Lote enviado",
          description: `Enviados: ${data?.enviados ?? 0} · Ignorados (já receberam): ${data?.ignorados ?? 0} · Pendentes: ${data?.pendentes ?? 0} · Falhas: ${data?.falhas ?? 0}`,
        });
      }
      refetch();
      refetchStats();
    } catch (e: any) {
      toast({ title: "Falha ao enviar", description: e.message ?? "Tente novamente.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const last = logs[0];
  const pending = last && last.is_complete === false ? last.pending_count ?? 0 : 0;
  const sentThisMonth = stats?.sentThisMonth ?? 0;
  const quotaLeft = Math.max(RESEND_MONTHLY_QUOTA - sentThisMonth, 0);
  const errorCount = logs.filter((l) => l.status === "error").length;


  return (
    <div className="space-y-6">
      <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Power className={`h-5 w-5 mt-0.5 ${isEnabled ? "text-primary" : "text-muted-foreground"}`} />
          <div>
            <p className="font-medium text-sm">Envio automático do digest</p>
            <p className="text-xs text-muted-foreground max-w-xl">
              A cada 5 dias enviamos as 5 vagas mais recentes para a base. Se não houver vaga nova
              desde o último envio, nada é disparado. Quando desligado, as execuções agendadas são
              registradas como "Ignorado".
            </p>
            {updatedAt && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Última alteração: {new Date(updatedAt).toLocaleString("pt-BR")}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="batch-limit" className="text-xs text-muted-foreground whitespace-nowrap">
              Lote por execução
            </Label>
            <Input
              id="batch-limit"
              type="number"
              min={10}
              max={2000}
              step={10}
              value={batchLimit}
              onChange={(e) => setBatchLimit(Math.max(10, Math.min(2000, Number(e.target.value) || 10)))}
              className="h-9 w-24"
            />
          </div>
          <Button size="sm" variant="outline" onClick={() => handleSendNow(false)} disabled={sending}>
            <Send className={`h-4 w-4 mr-1.5 ${sending ? "animate-pulse" : ""}`} />
            {sending ? "Enviando..." : "Enviar agora"}
          </Button>
          {pending > 0 && (
            <Button size="sm" onClick={() => handleSendNow(true)} disabled={sending}>
              <PlayCircle className="h-4 w-4 mr-1.5" />
              Continuar ({pending})
            </Button>
          )}
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={flagLoading || toggle.isPending}
          />
        </div>
      </Card>

      {pending > 0 && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5">
          <p className="text-sm font-medium text-amber-700">
            Envio parcial em aberto — {pending} destinatário(s) ainda não receberam este digest.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Use "Continuar" para enviar o próximo lote. Ninguém recebe a mesma vaga duas vezes.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Clock className="h-3.5 w-3.5" /> Último disparo
          </div>
          <p className="text-sm font-semibold mt-1">
            {last ? new Date(last.created_at).toLocaleString("pt-BR") : "Nunca"}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Mail className="h-3.5 w-3.5" /> Enviados no mês
          </div>
          <p className="text-2xl font-bold mt-1">{sentThisMonth}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            ~{quotaLeft} restantes na cota estimada ({RESEND_MONTHLY_QUOTA}/mês)
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Users className="h-3.5 w-3.5" /> Entregas registradas
          </div>
          <p className="text-2xl font-bold mt-1">{stats?.deliveriesTotal ?? 0}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">pares usuário + vaga já enviados</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <AlertTriangle className="h-3.5 w-3.5" /> Disparos com erro
          </div>
          <p className="text-2xl font-bold mt-1">{errorCount}</p>
        </Card>
      </div>


      <Card className="p-4">
        <h2 className="text-sm font-semibold mb-3">Prévia do próximo envio</h2>
        {previewLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : preview.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma vaga ativa disponível.</p>
        ) : (
          <ul className="space-y-2">
            {preview.map((job) => (
              <li key={job.id} className="flex flex-wrap items-center gap-2 text-sm border-b border-border/60 pb-2 last:border-0 last:pb-0">
                <span className="font-medium">{job.title}</span>
                <span className="text-muted-foreground">· {job.company_name}</span>
                {job.work_model && (
                  <Badge variant="secondary" className="text-[11px]">{job.work_model}</Badge>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                  {new Date(job.created_at).toLocaleDateString("pt-BR")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Histórico de disparos</h2>
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum disparo registrado ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Vagas</TableHead>
                  <TableHead className="text-right">Destinatários</TableHead>
                  <TableHead className="text-right">Enviados</TableHead>
                  <TableHead className="text-right">Ignorados</TableHead>
                  <TableHead className="text-right">Pendentes</TableHead>
                  <TableHead className="text-right">Falhas</TableHead>

                  <TableHead className="text-right">Duração</TableHead>
                  <TableHead>Mensagem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const errorList = Array.isArray(log.errors) ? (log.errors as string[]) : [];
                  const isOpen = expanded === log.id;
                  return (
                    <Fragment key={log.id}>
                      <TableRow
                        className={errorList.length > 0 ? "cursor-pointer" : undefined}
                        onClick={() => errorList.length > 0 && setExpanded(isOpen ? null : log.id)}
                      >
                        <TableCell className="whitespace-nowrap text-xs">
                          {new Date(log.created_at).toLocaleString("pt-BR")}
                        </TableCell>
                        <TableCell className="text-xs capitalize">{log.triggered_by}</TableCell>
                        <TableCell>{statusBadge(log.status)}</TableCell>
                        <TableCell className="text-right text-xs">{log.jobs_count}</TableCell>
                        <TableCell className="text-right text-xs">{log.recipients_count}</TableCell>
                        <TableCell className="text-right text-xs font-medium">{log.sent_count}</TableCell>
                        <TableCell className="text-right text-xs">{log.skipped_count ?? 0}</TableCell>
                        <TableCell className="text-right text-xs">{log.pending_count ?? 0}</TableCell>
                        <TableCell className="text-right text-xs">{log.failed_count}</TableCell>

                        <TableCell className="text-right text-xs">{formatDuration(log.duration_ms)}</TableCell>
                        <TableCell className="text-xs max-w-[260px] truncate text-muted-foreground">
                          {log.error_message ||
                            (errorList.length > 0 ? `${errorList.length} aviso(s) — clique para ver` : "-")}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={11} className="bg-muted/40">
                            <ul className="text-xs space-y-1 list-disc pl-5">
                              {errorList.map((err, i) => (
                                <li key={i} className="text-muted-foreground break-all">{String(err)}</li>
                              ))}
                            </ul>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
